import React from 'react';
import ReactPDF, { Image, StyleSheet, Text, View } from '@react-pdf/renderer';
import { BadgeQrCode } from '../badgeElement/qrCode/BadgeQrCode';
import { DownloadFileTypes, PAPER_SIZE, PDFEntitlementIcon } from '../../../types/enums';
import { PDFBadgeInfo } from '../../../types/badge';
import { SourceObject, Style } from '@react-pdf/types';
import { BadgeText } from '../badgeElement/text/BadgeText';
import { BadgeName } from '../badgeElement/name/BadgeName';
import { BadgeJobTitle } from '../badgeElement/jobTitle/BadgeJobTitle';
import { BadgeIcons } from '../badgeElement/icons/BadgeIcons';
import { useJapaneseBadgeElement } from '../badgeElement/hooks/useJapaneseBadgeElement';
import pdfStyles from '../pdfDocument/pdfStyles';

export interface JapaneseBadgeElementProps {
  logo: SourceObject;
  isCustomBadge: boolean;
  qrCodeUrl?: string;
  paperSize: PAPER_SIZE;
  badgeId: PDFBadgeInfo['badgeId'];
  additionalStyles: Style;
  companyNameTruncated: string;
  personData: {
    jobTitle: string;
    isSingleLineName: boolean;
    lastName: string;
    firstName: string;
  };
  badgeIcons: PDFEntitlementIcon[];
  type: DownloadFileTypes;
  rotateAngle?: number;
}

export const JapaneseBadgeElement: React.FC<JapaneseBadgeElementProps> = ({
    rotateAngle = 0,
    logo,
    isCustomBadge,
    qrCodeUrl,
    paperSize,
    badgeId,
    additionalStyles = {},
    companyNameTruncated,
    personData,
    badgeIcons,
    type
  }) => {
  const { isSingleLineName, firstName, lastName, jobTitle } = personData;

  const {
    getCompanyNameStyles,
    nameFontSize,
    getJobTitleStyles,
    getIconsContainerStyles,
    getBadgeIdStyles,
    getQrCodeStyles,
    getFirstNameStyles,
    getLastNameStyles,
    jobTitleFontSize
  } = useJapaneseBadgeElement(personData, paperSize, companyNameTruncated);

  return (
    <View style={[pdfStyles.japaneseBadgeItem, additionalStyles, isCustomBadge ? pdfStyles.badgeItemFullWidth : {}, rotateAngle ? {
      transform: `rotate(${rotateAngle}deg)`
    } : {}]}>
      <Image
        source={logo}
        style={{
          height: 'auto',
          width: '100%',
          maxHeight: type === DownloadFileTypes.ZPL ? '125px' : '160px'
        }}
      />
        <BadgeText
          text={companyNameTruncated}
          style={getCompanyNameStyles()}
        />
        <BadgeJobTitle
          jobTitle={jobTitle}
          isSingleLineName={true}
          fontSize={jobTitleFontSize}
          styles={getJobTitleStyles()}
        />
        <View style={[pdfStyles.nameView, { padding: '0', marginTop: paperSize === PAPER_SIZE.W298_X_100 ? 10 : 5 }]}>
          {!isSingleLineName && <BadgeName name={lastName} fontSize={nameFontSize} styles={getLastNameStyles()} />}
          <Text style={getFirstNameStyles()}>{firstName}</Text>
        </View>
        <BadgeQrCode
          isCustomBadge={isCustomBadge}
          qrCodeUrl={qrCodeUrl}
          isSingleLineName={isSingleLineName}
          paperSize={paperSize}
          qrCodeMarginTop={5}
          overrideStyles={getQrCodeStyles()}
        />
        <Text style={getBadgeIdStyles()}>{badgeId}</Text>
        <BadgeIcons
          paperSize={paperSize}
          pdfStyles={pdfStyles}
          icons={badgeIcons}
          isCustomBadge={isCustomBadge}
          containerStyles={getIconsContainerStyles()}
        />
    </View>
  );
};
